import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Eye, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { TableSortHeader, useTableSort } from "./TableSortHeader";
import { StatusFilter } from "./StatusFilter";

interface Assessment {
  id: string;
  name: string;
  email: string;
  practice_name: string | null;
  role: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
}

interface ComputedResult {
  assessment_id: string;
  archetype: {
    primary?: string;
    secondary?: string;
  } | null;
  domain_scores: Record<string, number>;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "complete", label: "Completed" },
  { value: "in_progress", label: "In Progress" },
];

export const AdminReportsTab = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [results, setResults] = useState<Record<string, ComputedResult>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();
  const { sortKey, sortDirection, handleSort, sortData } = useTableSort<Assessment>("created_at", "desc");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select('id, name, email, practice_name, role, status, completed_at, created_at')
        .order('created_at', { ascending: false });

      if (assessmentsError) throw assessmentsError;
      setAssessments(assessmentsData || []);

      const completedIds = assessmentsData?.filter(a => a.status === 'complete').map(a => a.id) || [];
      if (completedIds.length > 0) {
        const { data: resultsData } = await supabase
          .from('computed_results')
          .select('assessment_id, archetype, domain_scores')
          .in('assessment_id', completedIds);

        const resultsMap: Record<string, ComputedResult> = {};
        resultsData?.forEach(r => {
          resultsMap[r.assessment_id] = r as ComputedResult;
        });
        setResults(resultsMap);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load reports. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredAssessments = useMemo(() => {
    let result = assessments;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(assessment =>
        assessment.name?.toLowerCase().includes(searchLower) ||
        assessment.email?.toLowerCase().includes(searchLower) ||
        assessment.practice_name?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter(assessment => assessment.status === statusFilter);
    }

    return result;
  }, [assessments, searchTerm, statusFilter]);

  const sortedAssessments = useMemo(() => sortData(filteredAssessments), [filteredAssessments, sortKey, sortDirection]);

  const completedCount = assessments.filter(a => a.status === 'complete').length;
  const inProgressCount = assessments.filter(a => a.status !== 'complete').length;

  const formatArchetype = (archetype: string | null | undefined) => {
    if (!archetype) return "N/A";
    return archetype.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const viewReport = (assessmentId: string) => {
    window.open(`/inventory/report/${assessmentId}`, '_blank');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Assessment Reports</CardTitle>
            <CardDescription>
              {completedCount} completed, {inProgressCount} in progress
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
            <StatusFilter
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={STATUS_OPTIONS}
            />
            <Button variant="outline" size="icon" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : sortedAssessments.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {searchTerm || statusFilter !== "all" ? "No results match your filters" : "No assessments found"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <TableSortHeader
                      label="Name"
                      sortKey="name"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Email"
                      sortKey="email"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Practice"
                      sortKey="practice_name"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>Archetype</TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Status"
                      sortKey="status"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Started"
                      sortKey="created_at"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <TableSortHeader
                      label="Completed"
                      sortKey="completed_at"
                      currentSortKey={sortKey}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAssessments.map((assessment) => {
                  const result = results[assessment.id];
                  const primaryArchetype = result?.archetype?.primary;
                  
                  return (
                    <TableRow key={assessment.id}>
                      <TableCell className="font-medium">{assessment.name || "—"}</TableCell>
                      <TableCell>{assessment.email || "—"}</TableCell>
                      <TableCell>{assessment.practice_name || "—"}</TableCell>
                      <TableCell>
                        {primaryArchetype ? (
                          <Badge variant="secondary">
                            {formatArchetype(primaryArchetype)}
                          </Badge>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={assessment.status === 'complete' ? "default" : "outline"}>
                          {assessment.status === 'complete' ? "Completed" : "In Progress"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(assessment.created_at), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell>
                        {assessment.completed_at
                          ? format(new Date(assessment.completed_at), "dd MMM yyyy HH:mm")
                          : "—"
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        {assessment.status === 'complete' && (
                          <Button variant="ghost" size="sm" onClick={() => viewReport(assessment.id)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
